// MoodSleepCard — 1-tap mood + sleep capture on TodayScreen Zone A.
// Phase 5 PR-A §B1, Phase 8 design pass.
//
// Three rows:
//   1. Mood graduated-dot row (1..5, very-low → great). Five circles
//      with a fill that ramps from a muted surface tone to the copper
//      accent. Selected dot gets a 2px outer ring. Text labels sit
//      under each dot — no emoji. The dot row is a single radiogroup
//      with each button carrying an aria-label.
//   2. Sleep hours stepper (0..14h in 0.5 increments).
//   3. Sleep quality segmented control (5 dots).
//
// Save behaviour:
//   - Any change kicks a 500ms debounce.
//   - On flush the card GETs /api/logs/<today> to merge into the existing
//     row's full body shape (the POST /api/logs handler requires the
//     check-in fields even on partial updates), then POSTs the merged
//     body back. The audit_log gets a `daily_log_mood_sleep_set` entry
//     only when one of the three values actually changed.
//   - No modal, no Save button. The toast surfaces save errors only.
//
// Empty-state copy is "How was today?" Once any of the three is set,
// the header softens to "Today logged" and the card carries an Edit
// affordance (just the standard tap-to-change pattern — the controls
// stay live so the user can fix a misclick without entering edit mode).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { apiGet, apiPost, ApiClientError } from '../../lib/api';
import { useToast } from '../ui/Toast';
import { isQueuedOffline } from '../../lib/apiErrors';
import { useToday } from '../../lib/useToday';

const SAVE_DEBOUNCE_MS = 500;
// Phase 8 — replaced the emoji glyph row with a graduated-dot scale.
// Each entry maps 1..5 to a screen-reader label only; the visual is
// driven by .mood-dot CSS variants (one per data-value).
const MOOD_LEVELS: Array<{ value: 1 | 2 | 3 | 4 | 5; label: string }> = [
  { value: 1, label: 'Very low' },
  { value: 2, label: 'Low' },
  { value: 3, label: 'Okay' },
  { value: 4, label: 'Good' },
  { value: 5, label: 'Great' },
];

const SOWS_BLANK = {
  q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, q6: 0, q7: 0, q8: 0,
  q9: 0, q10: 0, q11: 0, q12: 0, q13: 0, q14: 0, q15: 0, q16: 0,
};

interface ExistingLog {
  date: string;
  sows_json: string;
  cravings: number;
  phq2_q1: number;
  phq2_q2: number;
  sleep_hours: number;
  sleep_quality: number;
  bowel: 'normal' | 'constipated' | 'loose';
  meds_taken_json: string;
  note: string | null;
  suicidal_ideation: 0 | 1;
  ssrs_ideation_level?: number | null;
  ssrs_behavior?: number | null;
  checkin_kind?: 'full' | 'minimum';
  mood?: number | null;
}

interface MoodSleepCardProps {
  onChange?: () => void;
}

export function MoodSleepCard({ onChange }: MoodSleepCardProps) {
  const today = useToday();
  const toast = useToast();

  const [mood, setMood] = useState<number | null>(null);
  const [sleepHours, setSleepHours] = useState<number>(7);
  const [sleepQuality, setSleepQuality] = useState<number>(3);
  const [hasSetMood, setHasSetMood] = useState(false);
  const [hasSetSleep, setHasSetSleep] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hold the most-recent existing-row snapshot so the debounced save can
  // POST the full body the /api/logs handler expects. Refreshed on mount
  // and whenever the date changes.
  const existingRef = useRef<ExistingLog | null>(null);
  const debounceRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  // Initial load — hydrate the card from any existing daily_log row for today.
  useEffect(() => {
    let cancelled = false;
    apiGet<ExistingLog | null>(`/api/logs/${today}`)
      .then((row) => {
        if (cancelled) return;
        existingRef.current = row ?? null;
        if (row) {
          if (row.mood != null) {
            setMood(row.mood);
            setHasSetMood(true);
          }
          if (typeof row.sleep_hours === 'number') {
            setSleepHours(row.sleep_hours);
            setHasSetSleep(true);
          }
          if (typeof row.sleep_quality === 'number') {
            setSleepQuality(row.sleep_quality);
          }
        }
        setHydrated(true);
      })
      .catch(() => {
        // Soft-fail — card stays in empty state and the next change
        // attempts to POST. Network errors get surfaced from the save path.
        setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [today]);

  const flushSave = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      // Re-pull the latest snapshot so concurrent edits from the full
      // check-in screen don't lose their non-mood/sleep fields when we
      // POST the merged body back.
      const fresh = await apiGet<ExistingLog | null>(`/api/logs/${today}`).catch(
        () => existingRef.current,
      );
      const base = fresh ?? existingRef.current;
      const sowsObj = base?.sows_json ? safeParseSows(base.sows_json) : SOWS_BLANK;
      const medsArr = base?.meds_taken_json ? safeParseMeds(base.meds_taken_json) : [];

      const body = {
        date: today,
        sows: sowsObj,
        cravings: base?.cravings ?? 0,
        phq2Q1: base?.phq2_q1 ?? 0,
        phq2Q2: base?.phq2_q2 ?? 0,
        sleepHours,
        sleepQuality,
        bowel: base?.bowel ?? 'normal',
        medsTaken: medsArr,
        note: base?.note ?? null,
        suicidalIdeation: (base?.suicidal_ideation ?? 0) as 0 | 1,
        ssrsIdeationLevel: base?.ssrs_ideation_level ?? 0,
        ssrsBehavior: (base?.ssrs_behavior ?? 0) as 0 | 1,
        checkinKind: base?.checkin_kind ?? 'minimum',
        mood: hasSetMood ? mood : null,
      };

      await apiPost('/api/logs', body);
      onChange?.();
    } catch (err) {
      if (isQueuedOffline(err)) {
        // Offline queue will replay — no toast.
        return;
      }
      const msg =
        err instanceof ApiClientError && err.status === 400
          ? 'Could not save — check the form.'
          : 'Could not save mood/sleep — try again.';
      toast.push(msg, { tone: 'error' });
    } finally {
      inFlightRef.current = false;
    }
  }, [today, hasSetMood, mood, sleepHours, sleepQuality, onChange, toast]);

  const scheduleSave = useCallback(() => {
    if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      void flushSave();
    }, SAVE_DEBOUNCE_MS);
  }, [flushSave]);

  // Flush any pending save on unmount so a tab-close mid-debounce still lands.
  useEffect(
    () => () => {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
        void flushSave();
      }
    },
    [flushSave],
  );

  function pickMood(v: 1 | 2 | 3 | 4 | 5) {
    setMood(v);
    setHasSetMood(true);
    scheduleSave();
  }

  function bumpSleep(delta: number) {
    setSleepHours((h) => {
      const next = Math.round((h + delta) * 2) / 2;
      const clamped = Math.min(14, Math.max(0, next));
      return clamped;
    });
    setHasSetSleep(true);
    scheduleSave();
  }

  function pickQuality(v: number) {
    setSleepQuality(v);
    setHasSetSleep(true);
    scheduleSave();
  }

  const headerLabel = useMemo(() => {
    if (!hydrated) return 'How was today?';
    if (hasSetMood || hasSetSleep) return 'Today logged';
    return 'How was today?';
  }, [hydrated, hasSetMood, hasSetSleep]);

  return (
    <Card className="mood-sleep-card" aria-label="Daily mood and sleep">
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBlockEnd: 'var(--space-3)' }}
      >
        <h3 className="text-h3" style={{ margin: 0 }}>{headerLabel}</h3>
        {hasSetMood || hasSetSleep ? (
          <span className="dim text-caption">tap to change</span>
        ) : null}
      </div>

      <div
        className="row"
        role="radiogroup"
        aria-label="Mood"
        style={{ gap: 'var(--space-2)', justifyContent: 'space-between', marginBlockEnd: 'var(--space-4)' }}
      >
        {MOOD_LEVELS.map((m) => {
          const selected = mood === m.value;
          return (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={m.label}
              onClick={() => pickMood(m.value)}
              className="mood-dot"
              data-state={selected ? 'on' : 'off'}
              data-value={m.value}
            >
              <span aria-hidden="true" className="mood-dot-fill" />
              <span className="mood-dot-label">{m.label}</span>
            </button>
          );
        })}
      </div>

      <div
        className="row"
        style={{ gap: 'var(--space-3)', alignItems: 'center', marginBlockEnd: 'var(--space-3)' }}
      >
        <span className="field-label" style={{ flex: 1 }}>Sleep</span>
        <button
          type="button"
          onClick={() => bumpSleep(-0.5)}
          aria-label="Decrease sleep hours"
          className="mood-sleep-step-btn"
          disabled={sleepHours <= 0}
        >
          <Icon icon={ChevronDown} size={18} />
        </button>
        <span aria-live="polite" style={{ minWidth: 56, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
          {sleepHours.toFixed(1)}h
        </span>
        <button
          type="button"
          onClick={() => bumpSleep(0.5)}
          aria-label="Increase sleep hours"
          className="mood-sleep-step-btn"
          disabled={sleepHours >= 14}
        >
          <Icon icon={ChevronUp} size={18} />
        </button>
      </div>

      <div
        className="row"
        role="radiogroup"
        aria-label="Sleep quality"
        style={{ gap: 'var(--space-2)', alignItems: 'center' }}
      >
        <span className="field-label" style={{ flex: 1 }}>Quality</span>
        {[1, 2, 3, 4, 5].map((q) => {
          const on = sleepQuality >= q;
          return (
            <button
              key={q}
              type="button"
              role="radio"
              aria-checked={sleepQuality === q}
              aria-label={`Sleep quality ${q} of 5`}
              onClick={() => pickQuality(q)}
              className="mood-quality-dot"
              data-state={on ? 'on' : 'off'}
            />
          );
        })}
      </div>
    </Card>
  );
}

function safeParseSows(s: string): typeof SOWS_BLANK {
  try {
    const parsed = JSON.parse(s) as Record<string, unknown>;
    const out: Record<string, number> = { ...SOWS_BLANK };
    for (const k of Object.keys(SOWS_BLANK)) {
      const v = parsed[k];
      if (typeof v === 'number' && v >= 0 && v <= 4) out[k] = Math.floor(v);
    }
    return out as typeof SOWS_BLANK;
  } catch {
    return SOWS_BLANK;
  }
}

function safeParseMeds(s: string): string[] {
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) {
      return parsed.filter((x): x is string => typeof x === 'string').slice(0, 50);
    }
  } catch {
    // fall through
  }
  return [];
}
